import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { postBrochures, clearBrochures } from '../../../../actions/brochure-actions';
import { Row, Column, Button, TextInput, Select, Checkbox, Fieldset, Alert } from '@pella/sash-framework';

import Image from '../../../common/image';
import EloquaForm from '../../../common/eloqua-form';

import styles from './index.module.scss';
import { eloquaTrackingUrl } from '../../../utilities/constants';

const OrderForm = ({ brochure, showForm, clearBrochureState, setSuccess }) => {
	const [values, setValues] = useState({
		First: '',
		Last: '',
		Company: '',
		Address1: '',
		Address2: '',
		City: '',
		State: '',
		Zip: '',
		Country: '',
		DayPhone: '',
		EveningPhone: '',
		Email: '',
	});
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();
	const brochureData = useSelector((state) => state.brochureData);

	useEffect(() => {
		document.getElementById('brochures-order-form')?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	useEffect(() => {
		if (brochureData.status === 200) {
			setSuccess(`Success! Your order for free Pella Brochures by mail has been placed successfully. Please allow 7-10 business days for delivery. For questions or concerns, contact us at 888-40-PELLA or help@pella.com.`);
			clearBrochureState();
			dispatch(clearBrochures());
			showForm(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [brochureData]);

	const onInputChange = (e) => {
		setValues({
			...values,
			[e.target.name]: e.target.value,
		});
	};

	const onNumberInputChange = (name, value) => {
		setValues({
			...values,
			[name]: value,
		});
	};

	const validateForm = (First, Last, Address1, City, State, Zip, Country, DayPhone, Email) => {
		let errors = [];

		if (First.length === 0) {
			errors.First = 'First name is required';
		}

		if (Last.length === 0) {
			errors.Last = 'Last name is required';
		}

		if (Address1.length === 0) {
			errors.Address1 = 'Address 1 is required';
		}

		if (City.length === 0) {
			errors.City = 'City is required';
		}

		if (State.length === 0) {
			errors.State = 'State is required';
		}

		if (Zip.length === 0) {
			errors.Zip = 'Postal code is required';
		}

		if (Country.length === 0) {
			errors.Country = 'Postal code is required';
		}

		if (DayPhone.length === 0) {
			errors.DayPhone = 'Preferred phone number is required';
		}

		if (Email.length === 0) {
			errors.Email = 'Email address is required';
		}

		return errors;
	};

	const onFormSubmit = (e) => {
		e.preventDefault();

		const { First, Last, Address1, City, State, Zip, Country, DayPhone, Email } = values;
		const errors = validateForm(First, Last, Address1, City, State, Zip, Country, DayPhone, Email);

		if (Object.keys(errors).length > 0) {
			if (typeof window !== 'undefined') {
				window.scrollTo(0, 0);
			}

			setErrors(errors);
		} else {
			const data = new FormData(e.target);
			const apiData = {
				...values,
				CustomerType: document.getElementById('CustomerType').value,
				project_type: document.getElementById('project_type').value,
				market_type: document.getElementById('market_type').value,
				project_time: document.getElementById('project_time').value,
				OptInValue: document.getElementById('yes').checked ? 'yes' : 'no',
				ProductCodes: [brochure.brochure_code],
			};

			axios
				.post(eloquaTrackingUrl, data)
				.then((res) => {
					if (res.status === 200) {
						dispatch(postBrochures(apiData));
					}
				})
				.catch();
		}
	};

	return (
		<div id='brochures-order-form'>
			<h1 className={styles.broForm_Heading}>Place Your Free Order</h1>
			<p className={styles.broForm_Description}>Please fill out the form below. Pella respects your privacy and will keep all information confidential.</p>

			<Row
				largeGutters={true}
				verticalGutters={true}
				className={styles.broForm_Row}
			>
				<Column
					xs={12}
					lg={4}
				>
					<div className={styles.broForm_RequestedBrochures}>
						<h3 className='s2-h3__alt'>Your Requested Brochures</h3>

						<Row verticalGutters={true}>
							{brochure.image && brochure.image[0] && (
								<Column
									xs='auto'
									lg={4}
									xl='auto'
								>
									<Image
										data={brochure.image[0]}
										className={styles.broForm_RequestedImage}
									/>
								</Column>
							)}

							<Column
								xs='fluid'
								lg={8}
								xl='fluid'
							>
								{brochure.heading && <p>{brochure.heading}</p>}

								<button
									onClick={() => {
										showForm(false);
										clearBrochureState();
									}}
									className={styles.broForm_TextLink}
								>
									Remove
								</button>
							</Column>
						</Row>

						<p>
							Prefer to start reading now?{' '}
							<button
								onClick={() => {
									showForm(false);
									clearBrochureState();
								}}
								className={styles.broForm_TextLink}
							>
								Return to brochures page
							</button>{' '}
							and click Download Brochure.
						</p>
					</div>
				</Column>

				<Column
					xs={12}
					lg={8}
				>
					{Object.keys(errors).length > 0 && (
						<Alert
							type='error'
							className='mb-4'
							hideClose
						>
							Please review all required fields for any errors below
						</Alert>
					)}

					<EloquaForm
						elqFormName='brochure-mail-request'
						className={styles.broForm_Form}
						onFormSubmit={(e) => onFormSubmit(e)}
					>
						<input
							type='hidden'
							name='brochureName'
							value={brochure.heading}
						/>

						<h3 className='s2-h3__alt'>Your Mailing Address</h3>
						<Row verticalGutters={true}>
							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='First'
									labelText='First Name'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.First}
									errorText={errors.First}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='Last'
									labelText='Last Name'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.Last}
									errorText={errors.Last}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='Company'
									labelText='Company'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='Address1'
									labelText='Address 1'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.Address1}
									errorText={errors.Address1}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='Address2'
									labelText='Address 2'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='City'
									labelText='City/Town'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.City}
									errorText={errors.City}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='State'
									labelText='State'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.State}
									errorText={errors.State}
								>
									<option>Please Select</option>
									<option value='AL'>Alabama</option>
									<option value='AK'>Alaska</option>
									<option value='AS'>American Samoa</option>
									<option value='AZ'>Arizona</option>
									<option value='AR'>Arkansas</option>
									<option value='CA'>California</option>
									<option value='CO'>Colorado</option>
									<option value='CT'>Connecticut</option>
									<option value='DE'>Delaware</option>
									<option value='DC'>District of Columbia</option>
									<option value='FM'>Federated States of Micronesia</option>
									<option value='FL'>Florida</option>
									<option value='GA'>Georgia</option>
									<option value='GU'>Guam</option>
									<option value='HI'>Hawaii</option>
									<option value='ID'>Idaho</option>
									<option value='IL'>Illinois</option>
									<option value='IN'>Indiana</option>
									<option value='IA'>Iowa</option>
									<option value='KS'>Kansas</option>
									<option value='KY'>Kentucky</option>
									<option value='LA'>Louisiana</option>
									<option value='ME'>Maine</option>
									<option value='MH'>Marshall Islands</option>
									<option value='MD'>Maryland</option>
									<option value='MA'>Massachusetts</option>
									<option value='MI'>Michigan</option>
									<option value='MN'>Minnesota</option>
									<option value='MS'>Mississippi</option>
									<option value='MO'>Missouri</option>
									<option value='MT'>Montana</option>
									<option value='NE'>Nebraska</option>
									<option value='NV'>Nevada</option>
									<option value='NH'>New Hampshire</option>
									<option value='NJ'>New Jersey</option>
									<option value='NM'>New Mexico</option>
									<option value='NY'>New York</option>
									<option value='NC'>North Carolina</option>
									<option value='ND'>North Dakota</option>
									<option value='MP'>Northern Mariana Islands</option>
									<option value='OH'>Ohio</option>
									<option value='OK'>Oklahoma</option>
									<option value='OR'>Oregon</option>
									<option value='PW'>Palau</option>
									<option value='PA'>Pennsylvania</option>
									<option value='PR'>Puerto Rico</option>
									<option value='RI'>Rhode Island</option>
									<option value='SC'>South Carolina</option>
									<option value='SD'>South Dakota</option>
									<option value='TN'>Tennessee</option>
									<option value='TX'>Texas</option>
									<option value='UT'>Utah</option>
									<option value='VT'>Vermont</option>
									<option value='VI'>Virgin Islands</option>
									<option value='VA'>Virginia</option>
									<option value='WA'>Washington</option>
									<option value='WV'>West Virginia</option>
									<option value='WI'>Wisconsin</option>
									<option value='WY'>Wyoming</option>
									<option value='AB'>Alberta</option>
									<option value='BC'>British Columbia</option>
									<option value='MB'>Manitoba</option>
									<option value='NB'>New Brunswick</option>
									<option value='NL'>Newfoundland and Labrador</option>
									<option value='NT'>Northwest Territories</option>
									<option value='NS'>Nova Scotia</option>
									<option value='NU'>Nunavut</option>
									<option value='ON'>Ontario</option>
									<option value='PE'>Prince Edward Island</option>
									<option value='QC'>Quebec</option>
									<option value='SK'>Saskatchewan</option>
									<option value='YT'>Yukon</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									id='Zip'
									labelText='Postal Code'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.Zip}
									errorText={errors.Zip}
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='Country'
									labelText='Country'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.Country}
									errorText={errors.Country}
								>
									<option>Please Select</option>
									<option value='USA'>USA</option>
									<option value='Canada'>Canada</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									type='tel'
									id='DayPhone'
									labelText='Day Phone'
									groupClass={styles.broForm_FieldGroup}
									onValueChange={(values) => onNumberInputChange('DayPhone', values.formattedValue)}
									required={true}
									error={errors.DayPhone}
									errorText={errors.DayPhone}
									format='(###) ###-####'
									allowEmptyFormatting
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									type='tel'
									id='EveningPhone'
									labelText='Evening Phone'
									groupClass={styles.broForm_FieldGroup}
									onValueChange={(values) => onNumberInputChange('EveningPhone', values.formattedValue)}
									format='(###) ###-####'
									allowEmptyFormatting
								/>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<TextInput
									type='email'
									id='Email'
									labelText='Email Address'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
									required={true}
									error={errors.Email}
									errorText={errors.Email}
								/>
							</Column>
						</Row>

						<h3 className={`${styles.broForm_OptionalHeading} s2-h3__alt`}>Tell Us More About Your Project (Optional)</h3>
						<Row verticalGutters={true}>
							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='CustomerType'
									labelText='I am a(n)'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								>
									<option>Please Select</option>
									<option value='1'>Homeowner</option>
									<option value='2'>Architect</option>
									<option value='4'>Builder</option>
									<option value='3'>Contractor</option>
									<option value='6'>Designer</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='project_type'
									labelText='My project is'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								>
									<option>Please Select</option>
									<option value='Commercial'>Commercial</option>
									<option value='Residential'>Residential</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='market_type'
									labelText='I am in the market for windows/doors because I am'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								>
									<option>Please Select</option>
									<option value='Building'>Building</option>
									<option value='Remodeling'>Remodeling</option>
									<option value='Replacing'>Replacing</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
								xl={4}
							>
								<Select
									id='project_time'
									labelText='Project Timing'
									groupClass={styles.broForm_FieldGroup}
									onChange={(e) => onInputChange(e)}
								>
									<option>Please Select</option>
									<option value='7'>Within 1 Week</option>
									<option value='8'>Within 1 Month</option>
									<option value='9'>1-3 Months</option>
									<option value='10'>3-6 Months</option>
									<option value='11'>6-12 Months</option>
									<option value='12'>More than 1 Year</option>
									<option value='13'>Unsecure/Undecided</option>
								</Select>
							</Column>

							<Column xs={12}>
								<Fieldset
									legendText='Would you like to be notified of Pella product updates or special offers via email?'
									className={styles.broForm_Fieldset}
								>
									<Checkbox
										type='radio'
										id='yes'
										name='OptInValue'
										labelText='Yes'
										defaultChecked={true}
										value='Yes'
									/>

									<Checkbox
										type='radio'
										id='no'
										name='OptInValue'
										labelText='No'
										value='No'
									/>
								</Fieldset>
							</Column>

							<Column xs={12}>
								<Button
									type='submit'
									color='primary'
								>
									Submit Request
								</Button>
							</Column>
						</Row>
					</EloquaForm>
				</Column>
			</Row>
		</div>
	);
};

OrderForm.propTypes = {
	brochure: PropTypes.object,
	showForm: PropTypes.func,
	clearBrochureState: PropTypes.func,
	setSuccess: PropTypes.func,
};

export default OrderForm;
